import { useCallback } from "react";
import { usePostHog, type PostHog } from "posthog-js/react";
import { useAtomValue } from "jotai";

import { useAccount } from "@/hooks";
import { languageVar } from "@/variables/globalVar";

export const CaptureEventType = {
  USER_SIGNED_UP: "USER_SIGNED_UP",
  USER_LOGGED_IN: "USER_LOGGED_IN",
  USER_LOGGED_OUT: "USER_LOGGED_OUT",
  USER_SIGNUP_PAGE_VISITED: "USER_SIGNUP_PAGE_VISITED",
  USER_LOGIN_PAGE_VISITED: "USER_LOGIN_PAGE_VISITED",
  USER_FORGOT_PASSWORD_PAGE_VISITED: "USER_FORGOT_PASSWORD_PAGE_VISITED",
  USER_CLICKED_HELP: "USER_CLICKED_HELP",
  USER_ADDRESS_SEARCH: "USER_ADDRESS_SEARCH",
  USER_ADDRESS_SUBMITTED: "USER_ADDRESS_SUBMITTED",
  USER_RESTARTED_CONVERSATION: "USER_RESTARTED_CONVERSATION",
  USER_OFF_TOPIC_RENDERED: "USER_OFF_TOPIC_RENDERED",
  USER_RESTARTED_CONVERSATION_FROM_OFF_TOPIC:
    "USER_RESTARTED_CONVERSATION_FROM_OFF_TOPIC",
  USER_SYSTEM_ERROR_RENDERED: "USER_SYSTEM_ERROR_RENDERED",
  USER_RESTARTED_CONVERSATION_FROM_SYSTEM_ERROR:
    "USER_RESTARTED_CONVERSATION_FROM_SYSTEM_ERROR",
  USER_QUESTION_RENDERED: "USER_QUESTION_RENDERED",
  USER_ANSWERED_QUESTION: "USER_ANSWERED_QUESTION",
  USER_CONFIRM_REPORT_RENDERED: "USER_CONFIRM_REPORT_RENDERED",
  USER_CONFIRM_REPORT_ACCEPTED: "USER_CONFIRM_REPORT_ACCEPTED",
  USER_REPORT_LOADER_RENDERED: "USER_REPORT_LOADER_RENDERED",
  USER_REPORT_REGENERATING_LOADER_RENDERED:
    "USER_REPORT_REGENERATING_LOADER_RENDERED",
  USER_REPORT_RENDERED: "USER_REPORT_RENDERED",
  USER_REPORT_VISIBLE_FOR_30_SECONDS: "USER_REPORT_VISIBLE_FOR_30_SECONDS",
  USER_ADJUST_PROPOSAL_RENDERED: "USER_ADJUST_PROPOSAL_RENDERED",
  USER_ADJUSTED_PROPOSAL: "USER_ADJUSTED_PROPOSAL",
  USER_REQUEST_COPY_RENDERED: "USER_REQUEST_COPY_RENDERED",
  USER_REQUESTED_COPY: "USER_REQUESTED_COPY",
  USER_CHECK_APPLICATION_REQUIREMENTS_RENDERED:
    "USER_CHECK_APPLICATION_REQUIREMENTS_RENDERED",
  USER_CHECK_APPLICATION_REQUIREMENTS_SUBMITTED:
    "USER_CHECK_APPLICATION_REQUIREMENTS_SUBMITTED",
  USER_CHECK_EXEMPTION_CRITERIA_RENDERED:
    "USER_CHECK_EXEMPTION_CRITERIA_RENDERED",
  USER_CHECK_EXEMPTION_CRITERIA_SUBMITTED:
    "USER_CHECK_EXEMPTION_CRITERIA_SUBMITTED",
  USER_LANGUAGE_CHANGED: "USER_LANGUAGE_CHANGED",
  USER_CLICKED_PERMIT_REQUIREMENTS: "USER_CLICKED_PERMIT_REQUIREMENTS",
  USER_CLICKED_APPLICATION_REQUIREMENTS:
    "USER_CLICKED_APPLICATION_REQUIREMENTS",
  USER_CLICKED_MISSING_INFORMATION: "USER_CLICKED_MISSING_INFORMATION",
  USER_CLICKED_LOT_AND_PROPOSAL: "USER_CLICKED_LOT_AND_PROPOSAL",
  USER_CLICKED_NEXT_STEPS: "USER_CLICKED_NEXT_STEPS",
  USER_CLICKED_SHARE: "USER_CLICKED_SHARE",
  USER_CLICKED_ADJUST_PROPOSAL: "USER_CLICKED_ADJUST_PROPOSAL",
  USER_CLICKED_COPY_SHARE_LINK: "USER_CLICKED_COPY_SHARE_LINK",
  USER_CLICKED_REPORT_LINK: "USER_CLICKED_REPORT_LINK",
} as const;
export type CaptureEventType =
  (typeof CaptureEventType)[keyof typeof CaptureEventType];

export function useCaptureEvent() {
  const posthog = usePostHog();

  const { conversation } = useAccount();

  const language = useAtomValue(languageVar);

  const captureEvent = useCallback(
    (
      eventName: CaptureEventType,
      properties?: Parameters<PostHog["capture"]>[1],
    ) => {
      const mergedProperties = {
        ...(conversation
          ? {
              conversationId: conversation?.id,
              identifier: conversation?.identifier,
              council_code: conversation?.council_code,
              address: conversation?.address,
            }
          : {}),
        language,
        ...properties,
      };

      return posthog.capture(eventName, mergedProperties);
    },
    [conversation, posthog],
  );

  return { captureEvent };
}
